/**
 * @author Karthik <karthik.x@314ecorp.com>
 * @description Integration config
 */

import includes from 'lodash/includes';
import some from 'lodash/some';

import Environment from 'utils/Environment';
import { AUTH_URL_INTEGRATION, DEFAULT_SERVER_URL, getServerUrl, POSTHOG_INTEGRATION_API_HOST_URL } from 'utils/url';
import { POSTHOG_EVENTS_TO_BLOCK, URL_BLOCKLIST } from 'utils/postHogUtils';
import { getClientId } from 'utils/util';

export interface EnvConfig {
	appName: string;
	keycloak: {
		config: import('keycloak-js').KeycloakConfig;
		initOptions: import('keycloak-js').KeycloakInitOptions;
	};
	client: import('axios').AxiosRequestConfig<any>;
	faro: {
		url: string;
		apiKey: string;
	};
	posthog: {
		apiKey: string;
		config: Partial<import('posthog-js').PostHogConfig>;
	};
}

export const getApexDomain = (): string => {
	const hostName = window.location.hostname;
	const apexDomain = hostName.split('.').slice(-2).join('.');
	return Environment.isLocalhost() ? 'zsegment.tech' : apexDomain;
};

const getAPIEndpoint = (): string => {
	const hostName = window.location.hostname;
	const tenant = hostName.split('.')[0];
	const apexDomain = hostName.split('.').slice(-2).join('.');
	try {
		const domain = window.localStorage.getItem('API_ENDPOINT');
		if (domain) {
			return domain;
		}
		throw new Error('404');
	} catch {
		return Environment.isLocalhost() ? DEFAULT_SERVER_URL : `https://${tenant}.api.${apexDomain}/api/v1`;
	}
};

export const getTenant = (shortForm: boolean = false): string => {
	try {
		const endpoint = getAPIEndpoint();
		if (Environment.isLocalhost()) {
			return 'test11';
		}
		if (endpoint) {
			const firstPart = new URL(endpoint).hostname.split('.')[0];
			return shortForm ? firstPart : `zsegment-${firstPart}`;
		}
		const tenantName = getServerUrl().split('.')[0];
		return shortForm ? tenantName : `zsegment-${tenantName}`;
	} catch {
		return new URL(DEFAULT_SERVER_URL).hostname.split('.')[0];
	}
};

export const getRealm = (): string => {
	try {
		const endpoint = getAPIEndpoint();
		if (Environment.isLocalhost()) {
			return 'test11';
		}
		if (endpoint) {
			const firstPart = new URL(endpoint).hostname.split('.')[0];
			return `${firstPart}`;
		}
		const tenantName = getServerUrl().split('.')[0];
		return `${tenantName}`;
	} catch {
		return `${new URL(DEFAULT_SERVER_URL).hostname.split('.')[0]}`;
	}
};

const config: EnvConfig = {
	appName: 'ZSegment-Web',
	keycloak: {
		config: {
			realm: getRealm(),
			url: AUTH_URL_INTEGRATION,
			clientId: getClientId(),
		},
		initOptions: {
			onLoad: 'login-required',
			token: '',
			enableLogging: true,
			checkLoginIframe: false,
		},
	},
	client: {
		baseURL: getAPIEndpoint(),
		headers: {
			'Content-Type': 'application/json',
		},
	},
	faro: {
		url: 'https://collector.314ecorp.tech/collect',
		apiKey: 'tX4Pe@NE',
	},
	posthog: {
		apiKey: 'phc_mr9TwRhMj0zLe4FVLLyf5Vqnczolu7hpxR2aVEaxQ8V',
		config: {
			api_host: POSTHOG_INTEGRATION_API_HOST_URL,
			autocapture: false,
			capture_pageview: false,
			opt_out_capturing_by_default: Environment.isLocalhost(),
			before_send: (cr) => (!includes(POSTHOG_EVENTS_TO_BLOCK, cr?.event) ? cr : null),
			session_recording: {
				recordBody: true,
				maskAllInputs: false,
				maskCapturedNetworkRequestFn: (data) =>
					some(URL_BLOCKLIST, (url) => includes(data.name, url)) ? null : data,
			},
		},
	},
};

export default config;
